import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton,
  Divider,
  Link
} from '@mui/material';
import {
  Search as SearchIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Dangerous as CriticalIcon,
  Computer as ComputerIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import vulnerabilityApiService from '../../services/vulnerabilities/VulnerabilityApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Vulnerabilities = () => {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [unknownVulnerabilities, setUnknownVulnerabilities] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filteredVulnerabilities, setFilteredVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingCVE, setDownloadingCVE] = useState(false);
  const [downloadingPackage, setDownloadingPackage] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all'); // Filter by severity
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 50,
    count: 0
  });
  const [expandedRow, setExpandedRow] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    filterVulnerabilities();
  }, [vulnerabilities, searchTerm, selectedSeverity]);

  const fetchAgents = async () => {
    try {
      const agentsData = await dashboardApiService.getAllAgents();
      setAgents(agentsData);
      
      // Set default agent selection to first agent
      if (agentsData && agentsData.length > 0 && !selectedAgent) {
        setSelectedAgent(agentsData[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  };

  const handleDownloadCVEReport = async () => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    try {
      setDownloadingCVE(true);
      setError(null);
      
      const response = await vulnerabilityApiService.downloadCVEReport(selectedAgent);
      
      // Check if report has vulnerabilities
      if (response.summary && response.summary.total_count === 0) {
        setError(`No vulnerabilities found for agent ${selectedAgent}. Report will contain empty data.`);
      }
      
      // Create blob and download
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cve_report_agent_${selectedAgent}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download CVE report: ${err.message}`);
      console.error('Error downloading CVE report:', err);
    } finally {
      setDownloadingCVE(false);
    }
  };

  const handleDownloadPackageReport = async () => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    try {
      setDownloadingPackage(true);
      setError(null);
      
      const response = await vulnerabilityApiService.downloadPackageReport(selectedAgent);
      
      // Check if report has packages
      if (response.summary && response.summary.total_vulnerable_packages === 0) {
        setError(`No vulnerable packages found for agent ${selectedAgent}. Report will contain empty data.`);
      }
      
      // Create blob and download
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `package_report_agent_${selectedAgent}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download package report: ${err.message}`);
      console.error('Error downloading package report:', err);
    } finally {
      setDownloadingPackage(false);
    }
  };


  const handleFetchVulnerabilities = async () => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the agent-specific endpoint
      const response = await vulnerabilityApiService.getVulnerabilitiesByAgent(selectedAgent);
      
      console.log('API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Is Array:', Array.isArray(response));
      
      // Debug: Check if references field exists in first item
      if (response && response.length > 0) {
        console.log('First vulnerability sample:', JSON.stringify(response[0], null, 2));
      }
      
      // Handle different response structures
      let vulnerabilityData = [];
      
      if (Array.isArray(response)) {
        console.log('Processing as direct array, length:', response.length);
        vulnerabilityData = response;
      } else if (response && Array.isArray(response.data)) {
        console.log('Processing as response.data array, length:', response.data.length);
        vulnerabilityData = response.data;
      } else if (response && response.results && Array.isArray(response.results)) {
        console.log('Processing as response.results array, length:', response.results.length);
        vulnerabilityData = response.results;
      } else if (response && response.vulnerabilities && Array.isArray(response.vulnerabilities)) {
        console.log('Processing as response.vulnerabilities array, length:', response.vulnerabilities.length);
        vulnerabilityData = response.vulnerabilities;
      } else {
        console.log('No valid data found, setting empty array');
        console.log('Response structure:', Object.keys(response || {}));
        console.log('Full response:', response);
        vulnerabilityData = [];
      }
      
      // Separate by severity - Pending are those with N/A or no severity
      const validVulnerabilities = vulnerabilityData.filter(vuln => 
        vuln.vulnerability?.severity && 
        vuln.vulnerability?.severity !== 'N/A' &&
        vuln.vulnerability?.severity !== ''
      );
      
      const pendingVulnerabilities = vulnerabilityData.filter(vuln => 
        !vuln.vulnerability?.severity || 
        vuln.vulnerability?.severity === 'N/A' ||
        vuln.vulnerability?.severity === ''
      );
      
      const pendingCount = pendingVulnerabilities.length;
      
      // Count by severity
      const severityCounts = {
        Critical: 0,
        High: 0,
        Medium: 0,
        Low: 0
      };
      
      validVulnerabilities.forEach(vuln => {
        const severity = vuln.vulnerability?.severity;
        if (severity && severityCounts.hasOwnProperty(severity)) {
          severityCounts[severity]++;
        }
      });
      
      console.log(`Total: ${vulnerabilityData.length}, Valid: ${validVulnerabilities.length}, Pending: ${pendingCount}`);
      console.log('Severity breakdown:', severityCounts);
      
      setVulnerabilities(validVulnerabilities);
      setUnknownVulnerabilities(pendingVulnerabilities);
      setPagination({
        currentPage: 1,
        totalPages: Math.ceil(validVulnerabilities.length / 50),
        totalItems: validVulnerabilities.length,
        itemsPerPage: 50,
        total: vulnerabilityData.length,  // Total including pending
        validTotal: validVulnerabilities.length,  // Valid CVEs with severity
        unknownCount: pendingCount,  // Pending evaluation count
        severityCounts: severityCounts
      });
    } catch (err) {
      setError('Failed to fetch vulnerabilities data');
      console.error('Vulnerabilities fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate fix instructions based on package
  const getFixInstructions = (vuln) => {
    const packageName = vuln?.package?.name || 'package';
    const cveId = vuln?.vulnerability?.cve || vuln?.vulnerability?.id || 'CVE-XXXX';
    
    const commands = [
      {
        label: 'Debian/Ubuntu',
        command: `sudo apt update && sudo apt install --only-upgrade ${packageName}`,
        optional: `sudo apt upgrade  # Optional: Upgrade all packages`
      },
      {
        label: 'RHEL/CentOS',
        command: `sudo yum update ${packageName}`,
        optional: `sudo yum upgrade  # Optional: Upgrade all packages`
      },
      {
        label: 'Alpine',
        command: `sudo apk upgrade ${packageName}`,
        optional: `sudo apk upgrade  # Optional: Upgrade all packages`
      }
    ];
    
    // Extract documentation links from references field
    let docs = [];
    const references = vuln?.vulnerability?.reference || vuln?.vulnerability?.references || '';
    
    if (references && typeof references === 'string' && references.length > 0) {
      const refLinks = references.split(',').map(ref => ref.trim()).filter(ref => ref.startsWith('http'));
      docs = refLinks;
    }
    
    // If no references found, generate documentation links based on CVE ID
    if (docs.length === 0) {
      if (cveId && cveId !== 'CVE-XXXX' && cveId !== 'N/A' && cveId.startsWith('CVE-')) {
        docs = [
          `https://nvd.nist.gov/vuln/detail/${cveId}`,
          `https://ubuntu.com/security/${cveId}`,
          `https://www.cve.org/CVERecord?id=${cveId}`
        ];
      }
    }
    
    return { commands, docs };
  };

  const handleCopyCommand = (command) => {
    navigator.clipboard.writeText(command);
  };

  const handleToggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const filterVulnerabilities = () => {
    let filtered;

    // Filter by severity or unknown
    if (selectedSeverity === 'unknown') {
      filtered = unknownVulnerabilities;
    } else if (selectedSeverity === 'all') {
      filtered = vulnerabilities;
    } else {
      filtered = vulnerabilities.filter(vuln => 
        vuln.vulnerability?.severity === selectedSeverity
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(vuln => 
        vuln.vulnerability?.cve?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.vulnerability?.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.vulnerability?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.agent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vuln.package?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredVulnerabilities(filtered);
    
    // Update pagination based on filtered results
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalPages: Math.ceil(filtered.length / prev.itemsPerPage),
      totalItems: filtered.length
    }));
  };


  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const getSeverityColor = (severity) => {
    if (!severity) return 'default';
    switch (severity.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity) => {
    if (!severity) return <InfoIcon className="severity-icon-info" />;
    switch (severity.toLowerCase()) {
      case 'critical': return <CriticalIcon className="severity-icon-critical" />;
      case 'high': return <ErrorIcon className="severity-icon-error" />;
      case 'medium': return <WarningIcon className="severity-icon-warning" />;
      case 'low': return <InfoIcon className="severity-icon-info" />;
      default: return <InfoIcon className="severity-icon-info" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getUniqueSeverities = () => {
    const severities = [...new Set(vulnerabilities.map(vuln => {
      const severity = vuln.vulnerability?.severity;
      return severity === '-' ? 'Unknown' : severity;
    }).filter(Boolean))];
    return severities.sort();
  };

  if (loading) {
    return (
      <Box className="vulnerabilities-loading">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading vulnerabilities...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="vulnerabilities-error">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="vulnerabilities">
      <Box className="vulnerabilities-header">
        <Typography variant="h4" className="vulnerabilities-title">
          CVE Detection
        </Typography>
        <Typography variant="body1" className="vulnerabilities-subtitle">
          Monitor and manage security vulnerabilities across your infrastructure
        </Typography>
      </Box>

      <Box className="vulnerabilities-controls">
        <Box className="vulnerabilities-filters">
          <TextField
            label="Search vulnerabilities..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="vulnerabilities-search"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" className="filter-dropdown" sx={{ minWidth: 200 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              label="Select Agent"
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name} ({agent.ip})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleFetchVulnerabilities}
            disabled={!selectedAgent || loading}
            className="fetch-button"
          >
            Fetch Vulnerabilities
          </Button>
          
          {vulnerabilities.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="success"
                onClick={handleDownloadCVEReport}
                disabled={downloadingCVE || downloadingPackage}
                startIcon={downloadingCVE ? <CircularProgress size={20} /> : <DownloadIcon />}
                sx={{ ml: 1 }}
              >
                {downloadingCVE ? 'Downloading...' : 'Download CVE Report'}
              </Button>
              
              <Button
                variant="outlined"
                color="info"
                onClick={handleDownloadPackageReport}
                disabled={downloadingCVE || downloadingPackage}
                startIcon={downloadingPackage ? <CircularProgress size={20} /> : <DescriptionIcon />}
                sx={{ ml: 1 }}
              >
                {downloadingPackage ? 'Downloading...' : 'Download Package Report'}
              </Button>
            </>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Critical Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('Critical')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'Critical' ? '3px solid #d32f2f' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#d32f2f', fontWeight: 700, mb: 1 }}>
                  {pagination.severityCounts?.Critical || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Critical - Severity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* High Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('High')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'High' ? '3px solid #f57c00' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#f57c00', fontWeight: 700, mb: 1 }}>
                  {pagination.severityCounts?.High || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High - Severity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Medium Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('Medium')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'Medium' ? '3px solid #fbc02d' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#fbc02d', fontWeight: 700, mb: 1 }}>
                  {pagination.severityCounts?.Medium || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Medium - Severity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Low Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('Low')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'Low' ? '3px solid #388e3c' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#388e3c', fontWeight: 700, mb: 1 }}>
                  {pagination.severityCounts?.Low || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Low - Severity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('unknown')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'unknown' ? '3px solid #9e9e9e' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#9e9e9e', fontWeight: 700, mb: 1 }}>
                  {pagination.unknownCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending - Evaluation
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Card */}
          <Grid item xs={12} sm={6} md={2}>
            <Card 
              onClick={() => setSelectedSeverity('all')}
              sx={{ 
                cursor: 'pointer',
                border: selectedSeverity === 'all' ? '3px solid #1976d2' : '1px solid #e0e0e0',
                transition: 'all 0.3s',
                '&:hover': { 
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>
                  {pagination.validTotal || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total - Valid CVEs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Info chip below cards */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Showing: ${filteredVulnerabilities.length} ${selectedSeverity === 'unknown' ? 'Pending' : selectedSeverity === 'all' ? 'Total' : selectedSeverity} vulnerabilities`} 
            icon={<ComputerIcon />}
            color="primary"
            size="small"
          />
        </Box>
      </Box>

      <TableContainer component={Paper} className="vulnerabilities-table-container">
        <Table className="vulnerabilities-table">
          <TableHead>
            <TableRow>
              <TableCell className="table-header-cell">Vulnerability</TableCell>
              <TableCell className="table-header-cell">Severity</TableCell>
              <TableCell className="table-header-cell">Agent</TableCell>
              <TableCell className="table-header-cell">Package</TableCell>
              <TableCell className="table-header-cell">CVE</TableCell>
              <TableCell className="table-header-cell">Fix Instructions</TableCell>
              <TableCell className="table-header-cell">Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVulnerabilities
              .slice((pagination.currentPage - 1) * pagination.itemsPerPage, pagination.currentPage * pagination.itemsPerPage)
              .map((vuln, index) => {
                const rowIndex = (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                const isExpanded = expandedRow === rowIndex;
                const fixData = getFixInstructions(vuln);
                
                return (
                  <React.Fragment key={vuln.id || index}>
                    <TableRow className="table-row">
                <TableCell className="table-cell vulnerability-description-cell">
                  <Box className="vulnerability-description-container">
                    <Typography className="vulnerability-title">
                      {vuln.vulnerability?.cve || vuln.vulnerability?.id || 'Unknown Vulnerability'}
                    </Typography>
                    {vuln.vulnerability?.description && (
                      <Typography className="vulnerability-description">
                        {truncateText(vuln.vulnerability.description, 80)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="severity-container">
                    {getSeverityIcon(vuln.vulnerability?.severity)}
                    <Chip 
                      label={vuln.vulnerability?.severity === '-' ? 'Unknown' : vuln.vulnerability?.severity || 'Unknown'}
                      color={getSeverityColor(vuln.vulnerability?.severity)}
                      size="small"
                      className="severity-chip"
                    />
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="agent-info-container">
                    <Box className="agent-name-ip">
                      <Typography variant="body2" className="agent-name">
                        {vuln.agent?.name || 'Unknown Agent'}
                      </Typography>
                      <Typography variant="caption" className="agent-ip">
                        IP: {vuln.agent?.ip || 'N/A'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" className="agent-id">
                      ID: {vuln.agent?.id || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="package-info-container">
                    <Typography variant="body2" className="package-name">
                      {vuln.package?.name || 'Unknown Package'}
                    </Typography>
                    {vuln.package?.version && (
                      <Typography variant="caption" className="package-version">
                        Version: {vuln.package.version}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="cve-info-container">
                    <Typography variant="body2" className="cve-id">
                      {vuln.vulnerability?.cve || vuln.vulnerability?.id || 'N/A'}
                    </Typography>
                    {vuln.vulnerability?.cvss && (
                      <Typography variant="caption" className="cvss-score">
                        CVSS: {vuln.vulnerability.cvss}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell className="table-cell">
                  <IconButton
                    size="small"
                    onClick={() => handleToggleRow(rowIndex)}
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleToggleRow(rowIndex)}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '11px',
                      py: 0.5,
                      px: 1,
                      ml: 1
                    }}
                  >
                    {isExpanded ? 'Hide Fix' : 'View Fix'}
                  </Button>
                </TableCell>
                <TableCell className="table-cell">
                  <Box className="timestamp-container">
                    <ScheduleIcon className="timestamp-icon" />
                    <Typography variant="body2" className="timestamp">
                      {formatTimestamp(vuln.timestamp || vuln.detected_at || new Date())}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              
              {/* Collapsible Row for Fix Instructions */}
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 2, bgcolor: '#f5f5f5', p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Fix Instructions
                      </Typography>
                      
                      {/* Fix Commands */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#1976d2' }}>
                          Commands:
                        </Typography>
                        {fixData.commands.map((cmd, idx) => (
                          <Box key={idx} sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 0.5 }}>
                              {cmd.label}:
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              bgcolor: '#1e1e1e', 
                              p: 1.5, 
                              borderRadius: 1,
                              mb: 0.5
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  color: '#d4d4d4',
                                  flex: 1,
                                  fontSize: '13px'
                                }}
                              >
                                {cmd.command}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyCommand(cmd.command)}
                                sx={{ color: '#90caf9', ml: 1 }}
                                title="Copy command"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: 'monospace', 
                                color: '#666',
                                fontSize: '11px',
                                fontStyle: 'italic',
                                display: 'block',
                                mt: 0.5
                              }}
                            >
                              {cmd.optional}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {/* Documentation Links */}
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#1976d2' }}>
                          Documentation:
                        </Typography>
                        {fixData.docs.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {fixData.docs.map((doc, idx) => (
                              <Link
                                key={idx}
                                href={doc}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: '13px',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                <OpenInNewIcon fontSize="small" />
                                {doc}
                              </Link>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#999' }}>
                            No documentation links available
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {pagination.total > 0 && (
        <Box className="vulnerabilities-pagination">
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" className="pagination-info">
              Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, pagination.total)} of {pagination.total} vulnerabilities
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="outlined"
                onClick={() => handlePageChange(null, pagination.page - 1)}
                disabled={pagination.page <= 1}
                size="small"
              >
                Previous
              </Button>
              
              <Pagination
                count={Math.ceil(pagination.total / pagination.size)}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
              
              <Button
                variant="outlined"
                onClick={() => handlePageChange(null, pagination.page + 1)}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.size)}
                size="small"
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {filteredVulnerabilities.length === 0 && !loading && (
        <Box className="no-vulnerabilities">
          <BugReportIcon className="no-vulnerabilities-icon" />
          <Typography variant="h6">No vulnerabilities found</Typography>
          <Typography variant="body2">
            {searchTerm || selectedAgent 
              ? 'Try adjusting your search or select a different agent' 
              : 'Select an agent and click Fetch to load vulnerabilities'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Vulnerabilities;
